import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, TagIcon, ArrowRightIcon, FileTextIcon, DownloadIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
const posts = [{
  tag: 'Governance',
  tagColor: 'bg-blue-100 text-nrs-blue',
  title: "The Governance Gap: What ServiceNow Impact Doesn't Cover",
  excerpt: "Your agency works with ServiceNow Impact. They've provided excellent product vision for ITSM, HRSD, and other modules. Your roadmap looks great on paper. But can you answer the critical governance questions that determine whether your platform succeeds long-term?",
  readTime: '8 min read'
}, {
  tag: 'Application Rationalization',
  tagColor: 'bg-orange-100 text-nrs-orange',
  title: 'Should This App Be on ServiceNow? A Framework for Independent Assessment',
  excerpt: 'Your SI wants to migrate 20 applications to ServiceNow. ServiceNow Impact says the platform can handle them. Your CIO asks: "But should we?" Most agencies lack an independent voice to answer that question objectively—and the cost of getting it wrong is significant.',
  readTime: '10 min read'
}, {
  tag: 'Data Governance',
  tagColor: 'bg-green-100 text-green-700',
  title: 'Data Governance for ServiceNow: Who Owns the Truth?',
  excerpt: "Your ITSM module shows 5,247 users. Your HRSD module shows 5,189. Active Directory has 5,412. Which number is correct? If you can't answer that question, you have a data governance problem—and it's costing you more than you think.",
  readTime: '7 min read'
}, {
  tag: 'Platform Operations',
  tagColor: 'bg-purple-100 text-purple-700',
  title: "The Platform Owner Your Agency Can't Afford to Hire (And How to Get One Anyway)",
  excerpt: '"Seeking ServiceNow Platform Owner. Must have: 10+ years experience, CAD certification, federal compliance expertise, TS/SCI clearance, willingness to work for GS-13 salary." Good luck. The math doesn\'t work—but there\'s a better model.',
  readTime: '6 min read'
}];
export function ThoughtLeadershipPage() {
  return <div className="w-full" data-id="element-967">
      {/* HERO */}
      <section className="bg-gradient-to-br from-nrs-navy to-nrs-blue py-16 px-4" data-id="element-968">
        <div className="max-w-7xl mx-auto text-center" data-id="element-969">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-970">
            <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-white mb-4" data-id="element-971">
              Thought Leadership
            </h1>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto" data-id="element-972">
              Practical insights on ServiceNow governance, platform stewardship,
              and federal IT strategy from the NRS team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FEATURED WHITE PAPER */}
      <section className="bg-nrs-light py-10 px-4 border-b border-gray-200" data-id="element-973">
        <div className="max-w-7xl mx-auto" data-id="element-974">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="bg-white rounded-xl border-2 border-nrs-orange overflow-hidden" data-id="element-975">
            <div className="grid grid-cols-1 lg:grid-cols-3" data-id="element-976">
              <div className="bg-nrs-orange p-8 flex flex-col justify-center" data-id="element-977">
                <FileTextIcon className="h-12 w-12 text-white mb-4" data-id="element-978" />
                <div className="text-white font-heading font-bold text-xs uppercase tracking-wider mb-2" data-id="element-979">
                  Featured White Paper
                </div>
                <h2 className="font-heading font-bold text-xl text-white leading-tight" data-id="element-980">
                  The Three Pillars of ServiceNow Governance: Technical Policy,
                  Data, and Strategic
                </h2>
              </div>
              <div className="lg:col-span-2 p-8" data-id="element-981">
                <div className="flex items-center gap-3 mb-4" data-id="element-982">
                  <span className="px-2 py-0.5 bg-nrs-orange/10 text-nrs-orange text-xs font-semibold rounded-full" data-id="element-983">
                    White Paper
                  </span>
                  <span className="text-gray-400 text-xs" data-id="element-984">35 pages</span>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4" data-id="element-985">
                  This paper introduces a governance framework that fills the
                  gaps between ServiceNow Impact's product vision and SI
                  implementation work. Federal agencies need three types of
                  governance that Impact doesn't provide: Technical Policy
                  Governance, Data Governance, and Strategic Governance.
                </p>
                <p className="text-gray-600 text-sm mb-6" data-id="element-986">
                  Includes maturity models for each governance pillar
                  (Crawl/Walk/Run progression), self-assessment tools,
                  implementation roadmaps, and case studies from federal
                  agencies.
                </p>
                <a href="#" className="inline-flex items-center gap-2 px-5 py-2.5 bg-nrs-orange text-white font-semibold text-sm rounded-md hover:bg-amber-500 transition-colors" data-id="element-987">
                  <DownloadIcon className="h-4 w-4" data-id="element-988" />
                  Download White Paper
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* BLOG POSTS */}
      <section className="bg-white py-16 px-4" data-id="element-989">
        <div className="max-w-7xl mx-auto" data-id="element-990">
          <h2 className="font-heading font-bold text-2xl text-nrs-navy mb-8" data-id="element-991">
            Latest Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-992">
            {posts.map((post, i) => <motion.article key={post.title} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-id="element-993">
                <div className="bg-nrs-light h-3" data-id="element-994" />
                <div className="p-6" data-id="element-995">
                  <div className="flex items-center gap-3 mb-3" data-id="element-996">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${post.tagColor}`} data-id="element-997">
                      {post.tag}
                    </span>
                    <div className="flex items-center gap-1 text-gray-400 text-xs" data-id="element-998">
                      <ClockIcon className="h-3 w-3" data-id="element-999" />
                      {post.readTime}
                    </div>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-nrs-navy mb-3 leading-snug" data-id="element-1000">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-5" data-id="element-1001">
                    {post.excerpt}
                  </p>
                  <button className="inline-flex items-center gap-2 text-nrs-blue font-semibold text-sm hover:text-nrs-orange transition-colors" data-id="element-1002">
                    Read Article
                    <ArrowRightIcon className="h-4 w-4" data-id="element-1003" />
                  </button>
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      {/* NEWSLETTER CTA */}
      <section className="bg-nrs-navy py-12 px-4" data-id="element-1004">
        <div className="max-w-2xl mx-auto text-center" data-id="element-1005">
          <h2 className="font-heading font-bold text-2xl text-white mb-3" data-id="element-1006">
            ServiceNow Governance Insights
          </h2>
          <p className="text-blue-200 text-sm mb-6" data-id="element-1007">
            Monthly newsletter with governance frameworks, case studies, and
            federal IT strategy. No spam.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-id="element-1008">
            <input type="email" placeholder="your.email@agency.gov" className="flex-1 px-4 py-2.5 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-nrs-orange" data-id="element-1009" />
            <button className="px-5 py-2.5 bg-nrs-orange text-white font-semibold text-sm rounded-md hover:bg-amber-500 transition-colors" data-id="element-1010">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>;
}